// The UI's only door to data. `HttpClient` talks to c2's Worker through vite's /api proxy and
// parses its SSE streams; `MockClient` (src/ui/mock) has the same surface. `resolveClient()`
// picks: VITE_MOCK=1 forces the mock, otherwise the API is tried once and the mock is the fallback
// (the header says which one is live, so a mock never ships silently).
import type { Transcript, TranscriptSummary, ThemesEvent, AskEvent } from '../contract'

export interface Client {
  readonly kind: 'api' | 'mock'
  listTranscripts(): Promise<TranscriptSummary[]>
  getTranscript(id: string): Promise<Transcript>
  createTranscript(title: string, text: string): Promise<Transcript>
  themes(transcriptIds: string[] | undefined, onEvent: (e: ThemesEvent) => void, signal?: AbortSignal): Promise<void>
  ask(question: string, onEvent: (e: AskEvent) => void, signal?: AbortSignal): Promise<void>
  reset(): Promise<void>
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}: ${(await res.text()).slice(0, 200)}`)
  return res.json() as Promise<T>
}

/** Parse a text/event-stream body, yielding the JSON of each `data:` block. */
async function readSse<E>(res: Response, onEvent: (e: E) => void, signal?: AbortSignal) {
  if (!res.ok || !res.body) throw new Error(`${res.status} ${res.statusText}`)
  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  const flush = (block: string) => {
    const data = block.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trim()).join('\n')
    if (data) onEvent(JSON.parse(data) as E)
  }
  while (true) {
    if (signal?.aborted) { await reader.cancel(); return }
    const { done, value } = await reader.read()
    if (done) break
    buf += dec.decode(value, { stream: true })
    let i: number
    while ((i = buf.indexOf('\n\n')) >= 0) { flush(buf.slice(0, i)); buf = buf.slice(i + 2) }
  }
  if (buf.trim()) flush(buf)
}

export class HttpClient implements Client {
  readonly kind = 'api' as const
  listTranscripts() { return fetch('/api/transcripts').then((r) => json<TranscriptSummary[]>(r)) }
  getTranscript(id: string) { return fetch(`/api/transcripts/${encodeURIComponent(id)}`).then((r) => json<Transcript>(r)) }
  createTranscript(title: string, text: string) {
    return fetch('/api/transcripts', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ title, text }) }).then((r) => json<Transcript>(r))
  }
  async themes(transcriptIds: string[] | undefined, onEvent: (e: ThemesEvent) => void, signal?: AbortSignal) {
    const res = await fetch('/api/themes', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'text/event-stream' }, body: JSON.stringify(transcriptIds?.length ? { transcriptIds } : {}), signal })
    await readSse<ThemesEvent>(res, onEvent, signal)
  }
  async ask(question: string, onEvent: (e: AskEvent) => void, signal?: AbortSignal) {
    const res = await fetch('/api/ask', { method: 'POST', headers: { 'content-type': 'application/json', accept: 'text/event-stream' }, body: JSON.stringify({ question }), signal })
    await readSse<AskEvent>(res, onEvent, signal)
  }
  async reset() { await json(await fetch('/api/reset', { method: 'POST' })) }
}

export async function resolveClient(): Promise<{ client: Client; reason: string }> {
  if (import.meta.env.VITE_MOCK === '1') {
    const { MockClient } = await import('./mock/client')
    return { client: new MockClient(), reason: 'VITE_MOCK=1' }
  }
  const http = new HttpClient()
  try {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(), 2500)
    const res = await fetch('/api/transcripts', { signal: ctl.signal })
    clearTimeout(timer)
    if (!res.ok) throw new Error(`${res.status}`)
    await res.json()
    return { client: http, reason: '' }
  } catch (err) {
    const { MockClient } = await import('./mock/client')
    return { client: new MockClient(), reason: `API unreachable (${err instanceof Error ? err.message : err})` }
  }
}
